import React from "react";
import Box from "@material-ui/core/Box";
import { connect, ConnectedProps } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router";
import { ApplicationState, remoteActions, BASE_ROUTE, DONE_ROUTE } from "../../store";
import ContentComponent from "../../components/ContentComponent";
import { ContactSectionWithStartDate } from "../../components/ContactSection";
import { PriceText } from "../../components/PriceText";
import { Summary } from "../../components/Summary";
import NavToButton from "../../components/Buttons/NavToButton";
import { Typography, makeStyles, useTheme, SvgIcon } from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import CompositionBox from "../../components/CompositionBox";
import { TextButton } from "../../components/Buttons";
import PaymentOptions from "../../components/PaymentOptions";
import { Info } from "../../components/Info";
import DynamicDivider from "../../components/DynamicDivider";
import { MessageBox } from "../../components/MessageBox";
import CheckoutPageBottomLinks from "../../components/CheckoutPageBottomLinks";
import clsx from "clsx";
import { BrandAreaEnum, BrandsEnum, GetBrandArea, IsCommercialBrand } from "../../enums";
import { Theme } from "../../.themes";
import Header from "../../components/Header";
import registerVwoConversion from "../../helpers/vwo/registerVwoConversion";

const VWO_PURCHASE_CONFIRMATION_GOAL_ID = 2;

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    backgroundColor: theme.palette.backgroundSecondary?.default,
    textAlign: theme.typography.h1.textAlign,
    padding: `${theme.spacing(3)}px 0`,
    marginBottom: "calc(-2rem - 1px)", // funny hack to fix big spacing for volvia, and merge divider line with background for other brands
    "& hr": {
      border: "none",
      width: "75px",
      backgroundColor: "transparent",
      margin: 0,
    },
    ...theme.header,
  },
  divider: {
    margin: "2rem auto",
    ...theme.divider,
  },
  pageHeader: {
    ...theme.CheckoutPage?.pageHeader,
  },
  priceText: {
    ...theme.CheckoutPage?.priceText,
  },
  priceTopText: {
    ...theme.CheckoutPage?.topPriceText,
  },
  priceBoxHeader: {
    textAlign: "center",
    marginBottom: 16,
  },
  backButton: {
    minHeight: 0,
    padding: 0,
    ...theme.CheckoutPage?.backButton,
  },
  messageBox: {
    marginTop: `${theme.spacing(4)}px`,
  },
  dontShowPrice: {
    display: "none",
  },
  priceTextWithSecondaryPrice: {
    marginBottom: 0,
  },
  secondaryPrice: {
    float: "right",
    fontWeight: 400,
    marginTop: -20,
    "& .priceText": {
      fontSize: "16px",
    },
    "& .unitText": {
      fontSize: "12px",
    },
  },
  bottomTotalPriceText: {
    ...theme.CheckoutPage?.bottomTotalPriceText,
  },
  totalPriceText: {
    color: theme.priceText?.totalPriceColor,
  },
  layout: {
    backgroundColor: theme?.appBackgroundColor ?? "#fff",
  },
}));

class Checkout extends React.PureComponent<AppProps> {
  private hasTrackedVwoConversion = false;

  private handleCompleted = () => {
    if (!this.hasTrackedVwoConversion) {
      registerVwoConversion(VWO_PURCHASE_CONFIRMATION_GOAL_ID);
      this.hasTrackedVwoConversion = true;
    }

    this.props.history.push(DONE_ROUTE);
  };

  public componentDidMount() {
    if (this.props.completed) {
      this.handleCompleted();
    } else {
      if (!this.props.initialized) {
        this.props.history.push(BASE_ROUTE);
      } else {
        //the history push state only works if webshop is NOT inside iframe
        //so it fires only for non MO brands #739493
        if (window.self === window.top) {
          window.history.pushState({ page: 1 }, "Webshop", "");
        }
      }
    }
    window.addEventListener("popstate", () => {
      //redirect to first webshop page when back button is pushed in browser
      //fired only for non Mo brands (because push State above fired only then)
      //#739493
      this.props.history.push(BASE_ROUTE);
    });
  }

  public componentDidUpdate() {
    if (this.props.completed) {
      this.handleCompleted();
    }
  }
  public render() {
    return !this.props.completed && <CheckoutPage {...this.props} />;
  }
}

export function CheckoutPage(props: AppProps) {
  const theme: Theme = useTheme();
  const classes = useStyles(theme);

  const prices = props.footer?.prices || [];
  const lastIndex = (prices.length || 0) - 1;
  type IsRullaUt = true | false | null | undefined;
  const isRullaUt: IsRullaUt = props.isDisabled;
  const secondaryPrice = props.coverage?.Coverages?.find(x => x.InternalCode === props.coverage?.selectedCode)?.SecondaryPrice;
  const secondaryPriceText = secondaryPrice?.Amount + " " + secondaryPrice?.Currency + "/" + secondaryPrice?.Period;
  let showSecondaryPrice = false;
  if (isRullaUt) {
    switch (theme.brand) {
      case BrandsEnum.Bmw:
      case BrandsEnum.Mini:
        showSecondaryPrice = true;
        break;
    }
  }
  const allPrices = prices.map((item, index) => {
    let textClass: string | undefined = undefined;
    const isTotal = index === lastIndex;
    if (!isTotal) {
      textClass = clsx(classes.priceText);
    }
    return (
      <div>
        <PriceText
          key={index}
          text={item.title}
          priceText={item.price}
          isTotal={theme.brand !== BrandsEnum.Renault && isTotal}
          textVariant={isTotal ? "h5" : undefined}
          textClassName={textClass}
          priceTextClass={
            index !== lastIndex && GetBrandArea(theme.brand ?? BrandsEnum.None) === BrandAreaEnum.IMF
              ? clsx(classes.dontShowPrice)
              : clsx(classes.priceTopText)
          }
        />
      </div>
    );
  });
  const lastPrice = prices[lastIndex] || { title: "", price: "" };
  let backButtonColor: "primary" | "secondary" = "primary";
  switch (theme.brand) {
    case BrandsEnum.Nissan:
    case BrandsEnum.Kia:
    case BrandsEnum.Renault:
      backButtonColor = "secondary";
      break;
  }
  const icon = (theme.icons?.backIcon && (
    <SvgIcon fontSize='large' color='primary' className={clsx("arrowBack")}>
      {theme.icons?.backIcon}
    </SvgIcon>
  )) ?? <ArrowBack fontSize='large' color={backButtonColor} className={clsx("arrowBack")} />;
  localStorage.setItem("Webshop Page", "Checkout");

  const renderTotalPriceTooltip = () => {
    if (props.inputs.brand === "polestarforetag" && !props.profile?.isCarNew) return null;
    return <Info title={props.footer?.totalPriceTooltip} key={1} />;
  };

  return (
    <div className={classes.layout}>
      {props.decoupledRendering?.isDecoupledRendering ? <Header /> : null}
      <Box component='div' display='flex' flexDirection='column'>
        <div className={classes.header}>
          <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
            <div className={classes.priceBoxHeader}>
              <Typography variant='h1' component='h1' className={classes.pageHeader}>
                {props.texts?.coverageHeader}
              </Typography>
            </div>
            <hr />
            <div className='price-box'>
              <ContentComponent
                prices={[
                  <Summary summary={allPrices} key={0} />, // absence of key={} was causing errors in console
                  renderTotalPriceTooltip(),
                ]}
              />
              {secondaryPrice?.Amount && secondaryPrice?.Amount !== "0" && (
                <Typography variant={"h5"} className={classes.secondaryPrice}>
                  <span className={"priceText"}>{secondaryPrice?.Amount} </span>
                  <span className={"unitText"}>
                    {secondaryPrice?.Currency}/{secondaryPrice?.Period}
                  </span>
                </Typography>
              )}
            </div>
          </CompositionBox>
        </div>
        <DynamicDivider maxWidth={1200} className={classes.divider} />
        <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
          <div style={{ textAlign: "center" }}>
            <TextButton
              id='go_back'
              onClick={() => props.history.goBack()}
              color={backButtonColor}
              startIcon={icon}
              className={classes.backButton}
            >
              {props.texts?.changeChoiceText}
            </TextButton>
          </div>
          {!IsCommercialBrand(props.inputs.brand as BrandsEnum) && (
            <Box className={classes.messageBox}>
              <MessageBox>{props.texts?.trialPeriodMessage}</MessageBox>
            </Box>
          )}
        </CompositionBox>
        <DynamicDivider maxWidth={1200} className={classes.divider} />
        <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
          <ContactSectionWithStartDate />
        </CompositionBox>
        <DynamicDivider maxWidth={1200} className={classes.divider} />
        <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
          <PaymentOptions />
        </CompositionBox>
        <DynamicDivider maxWidth={1200} className={classes.divider} />
        <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
          <PriceText
            textVariant='h5'
            text={lastPrice.title ?? ""}
            priceText={lastPrice.price ?? ""}
            justifyContent='space-between'
            wrapperClassName={showSecondaryPrice ? clsx(classes.priceTextWithSecondaryPrice) : ""}
            textClassName={classes.bottomTotalPriceText}
            showSecondaryPrice={true}
            secondaryPrice={secondaryPrice}
          />
          {showSecondaryPrice && (
            <PriceText
              textVariant='h5'
              justifyContent='space-between'
              text=''
              priceText={secondaryPriceText ?? ""}
              priceTextClass={clsx(classes.secondaryPrice)}
            />
          )}
        </CompositionBox>
        <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
          <NavToButton onClick={props.purchase} icon={false} id='confirm_purchase'>
            {props.footer?.purchaseButton}
          </NavToButton>
          <CheckoutPageBottomLinks />
        </CompositionBox>
      </Box>
    </div>
  );
}

const connector = connect(
  (state: ApplicationState) => ({
    texts: state.texts,
    footer: state.footer,
    completed: state.ui.completed,
    initialized: state.ui.initialized,
    webshopMode: state.inputs.webshopMode,
    isDisabled: state.insurance?.startDate.isDisabled,
    coverage: state.insurance?.coverage,
    inputs: state.inputs,
    decoupledRendering: state.inputs.decoupledRendering,
    serverError: state.error,
    profile: state.profile,
  }),
  {
    purchase: remoteActions.purchase,
  },
);

type AppProps = ConnectedProps<typeof connector> & RouteComponentProps;

export default withRouter(connector(Checkout));
